import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Chip,
  Box,
  Alert,
  Avatar,
  Pagination as MuiPagination,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Remove as RemoveIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import { useCompanyCore } from '../../../../core/modules/company';
import { useAdjustmentsCore } from '../../../../core/modules/adjustments';
import { ArticleForAdjustment } from '../../../../core/modules/adjustments/interfaces';

interface AdjustmentsListProps {
  onOpenAdjustmentDialog: (articles: ArticleForAdjustment[], initialType?: 'reduction' | 'container_origin_change') => void;
}

export const AdjustmentsList: React.FC<AdjustmentsListProps> = ({
  onOpenAdjustmentDialog,
}) => {
  const { companyId } = useCompanyCore();
  const {
    articles,
    selectedArticles,
    pagination,
    loading,
    error,
    filters,
    loadArticles,
    handleSelectArticle,
    handleUnselectArticle,
    handleSelectAllArticles,
    handleClearSelection,
    isArticleSelected,
    selectedArticlesData,
  } = useAdjustmentsCore();

  // Type assertions para evitar errores de TypeScript
  const articlesList = articles as ArticleForAdjustment[];
  const selectedArticlesList = selectedArticles as number[];
  const paginationData = pagination as { total_elements: number; size: number; page: number } | null;
  const selectedArticlesDataList = selectedArticlesData as ArticleForAdjustment[];

  useEffect(() => {
    if (companyId) {
      // Listar solo si hay filtro primario seleccionado
      if ((filters as any)?.location_id || (filters as any)?.container_id) {
        loadArticles(companyId, { page: (paginationData?.page || 1) });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, (filters as any)?.location_id, (filters as any)?.container_id]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      handleSelectAllArticles();
    } else {
      handleClearSelection();
    }
  };

  const handleArticleSelect = (articleId: number) => {
    if (isArticleSelected(articleId)) {
      handleUnselectArticle(articleId);
    } else {
      handleSelectArticle(articleId);
    }
  };

  const handleAdjustSelected = () => {
    if (selectedArticlesDataList.length > 0) {
      onOpenAdjustmentDialog(selectedArticlesDataList, 'reduction');
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    if (companyId) {
      loadArticles(companyId, { page: newPage });
    }
  };

  const getTotalPages = () => {
    if (!paginationData) return 1;
    return Math.ceil(paginationData.total_elements / paginationData.size);
  };

  const isAllSelected = articlesList.length > 0 && selectedArticlesList.length === articlesList.length;
  const isIndeterminate = selectedArticlesList.length > 0 && selectedArticlesList.length < articlesList.length;

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress size={40} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{String(error)}</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Artículos para Ajuste ({paginationData?.total_elements || 0})
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          {selectedArticlesList.length > 0 && (
            <Chip
              label={`${selectedArticlesList.length} seleccionados`}
              color="primary"
              variant="outlined"
              onDelete={handleClearSelection}
            />
          )}
          <IconButton
            color="primary"
            disabled={selectedArticlesList.length === 0}
            onClick={handleAdjustSelected}
            size="large"
          >
            <Tooltip title="Reajustar seleccionados">
              <EditIcon />
            </Tooltip>
          </IconButton>
        </Box>
      </Box>

      {selectedArticlesList.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {selectedArticlesList.length} artículo(s) seleccionado(s) para ajuste
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  disabled={!articlesList || articlesList.length === 0}
                />
              </TableCell>
              <TableCell>Artículo</TableCell>
              <TableCell>Código</TableCell>
              {/* Columnas de ubicación/contenedor actuales se omiten en ajustes */}
              <TableCell>Proveedor Origen</TableCell>
              <TableCell align="center">Cantidad</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articlesList?.map((article) => (
              <TableRow key={article.id} hover selected={isArticleSelected(article.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isArticleSelected(article.id)}
                    onChange={() => handleArticleSelect(article.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      src={article.files?.[0]?.URL}
                      alt={article.name || article.description}
                      sx={{ width: 40, height: 40 }}
                    >
                      {(article.name || article.description).charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {article.name || article.description}
                      </Typography>
                      {article.description && (
                        <Typography variant="caption" color="text.secondary">
                          {article.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {article.code}
                  </Typography>
                </TableCell>
                {/* Ubicación/Contenedor actuales removidos */}
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {article.origin_provider_name || (article as any).provider_name || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight="medium" color="primary">
                    {article.quantity}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box >
                    {/* Botones directos: abrir modal con tab correspondiente */}
                    <Tooltip title="Rebajar producto">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onOpenAdjustmentDialog([article], 'reduction')}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Tooltip>
                    {/* <Tooltip title="Cambiar origen">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => onOpenAdjustmentDialog([article], 'container_origin_change')}
                      >
                        <SwapIcon />
                      </IconButton>
                    </Tooltip> */}
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {(!articlesList || articlesList.length === 0) && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  {!(filters as any)?.location_id && !(filters as any)?.container_id ? (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                      <Typography color="text.secondary" variant="h6">
                        Selecciona una ubicación o contenedor para ver los artículos disponibles
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        Usa los filtros primarios para cargar los artículos a ajustar
                      </Typography>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      No se encontraron artículos para ajuste
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {paginationData && paginationData.total_elements > 0 && getTotalPages() > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <MuiPagination
            count={getTotalPages()}
            page={paginationData.page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {selectedArticlesList.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Chip
            label={`Ajustar ${selectedArticlesList.length} artículo(s)`}
            color="primary"
            onClick={handleAdjustSelected}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      )}
    </Paper>
  );
}; 